import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Container,
  Divider,
  Paper,
  ToggleButton,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import scheduleIcon from '../images/schedule-time.png';
import gateIcon from '../images/gate.png';

interface RouteData {
  routeReference: string;
  vehicleRegistration: string | null;
  expiresAt: string;
  boundAt: string | null;
  transportationDate: string;
  departureCity: string | null;
  arrivalCity: string | null;
  arrivalTime: string | null;
  departureTime: string | null;
  status: string;
  legs: RouteLegData[];
}

interface RouteLegData {
  id: number;
  isCheckpoint: boolean;
  locationName: string;
  locationCity: string | null;
  terminalStatus?: string | null;
  terminalStatusDescription: string | null;
  gateNumber: string | null;
  carGateNumber: string | null;
  trailerGateNumber: string | null;
  carRegistrationNumber: string | null;
  trailerRegistrationNumber: string | null;
  carContainerNumber: string | null;
  trailerContainerNumber: string | null;
  dayNumber: number;
  transportationDate: string;
  scheduledArrival: string | null;
  scheduledDeparture: string | null;
  actualArrival: string | null;
  actualDeparture: string | null;
  actualDock: string | null;
  actualUndock: string | null;
  load: boolean;
  unload: boolean;
  isLoadingInCar: boolean;
  isLoadingInTrailer: boolean;
}

type PageState = 'loading' | 'binding' | 'success' | 'error';

type LoadingContainer = {
  type: string;
  reg: string | null;
  container: string | null;
  gate: string | null;
  scheduledTime: string;
  actualTime: string;
};

const EMPTY_TIME = '--:--';

const CHECKPOINT_ACTIONS = [
  { status: 'Arrived', label: 'Arrived', field: 'actualArrival' },
  { status: 'Ongoing', label: 'Dock', field: 'actualDock' },
  { status: 'Done', label: 'Undock', field: 'actualUndock' },
  { status: 'Departure', label: 'Departed', field: 'actualDeparture' },
] as const;

const formatTimeValue = (value?: string | null) => {
  if (!value) {
    return EMPTY_TIME;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return EMPTY_TIME;
  }

  const timeMatch = trimmedValue.match(/(\d{2}:\d{2})(?::\d{2})?/);
  if (timeMatch?.[1]) {
    return `${timeMatch[1]}`;
  }

  const parsedDate = new Date(trimmedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  return `${trimmedValue}`;
};

const formatDateTimeValue = (value: string) => {
  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export function PublicDriverPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [expandedLegId, setExpandedLegId] = useState<number | null>(null);
  const [savingLegId, setSavingLegId] = useState<number | null>(null);
  const [locationAccessDisabled, setLocationAccessDisabled] = useState<
    Record<number, boolean>
  >({});
  const [updateError, setUpdateError] = useState<{
    legId: number;
    message: string;
  } | null>(null);
  const updatePending = useRef(false);

  const getCurrentGeoLocation = async (): Promise<{
    latitude: number | null;
    longitude: number | null;
    accuracyMeters?: number;
    capturedAt?: string;
    accessDisabled: boolean;
  }> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null, accessDisabled: true });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
            capturedAt: new Date(position.timestamp).toISOString(),
            accessDisabled: false,
          });
        },
        (error) => {
          resolve({
            latitude: null,
            longitude: null,
            accessDisabled: error.code === error.PERMISSION_DENIED,
          });
        },
        { timeout: 10000, maximumAge: 0 },
      );
    });
  };

  const updateCheckpointStatus = async (
    leg: RouteLegData,
    action: (typeof CHECKPOINT_ACTIONS)[number],
  ) => {
    if (updatePending.current) {
      return;
    }
    updatePending.current = true;
    setSavingLegId(leg.id);
    setUpdateError(null);
    try {
      const selecting = !leg[action.field];
      const geoLocation = selecting
        ? await getCurrentGeoLocation()
        : { latitude: null, longitude: null, accessDisabled: false };
      if (selecting) {
        setLocationAccessDisabled((current) => ({
          ...current,
          [leg.id]: geoLocation.accessDisabled,
        }));
      }
      const response = await axios.post<{
        success: boolean;
        data: RouteLegData;
      }>(
        `/pappv2/api/public/driver-links/${token}/legs/${leg.id}/checkpoint-status`,
        {
          status: action.status,
          selected: selecting,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          geoLocation: selecting
            ? {
                latitude: geoLocation.latitude,
                longitude: geoLocation.longitude,
                ...(geoLocation.accuracyMeters !== undefined && {
                  accuracyMeters: geoLocation.accuracyMeters,
                }),
                ...(geoLocation.capturedAt && {
                  capturedAt: geoLocation.capturedAt,
                }),
              }
            : null,
        },
        { withCredentials: true },
      );
      if (!response.data.success || response.data.data?.id !== leg.id) {
        throw new Error('Failed to save checkpoint status. Please try again.');
      }
      const updatedLeg = response.data.data;
      setRouteData((current) =>
        current
          ? {
              ...current,
              legs: current.legs.map((item) =>
                item.id === updatedLeg.id ? updatedLeg : item,
              ),
            }
          : current,
      );
    } catch (error: unknown) {
      const apiMessage = axios.isAxiosError(error)
        ? error.response?.data?.message
        : undefined;
      setUpdateError({
        legId: leg.id,
        message:
          typeof apiMessage === 'string'
            ? apiMessage
            : 'Failed to save checkpoint status. Please try again.',
      });
    } finally {
      updatePending.current = false;
      setSavingLegId(null);
    }
  };

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('Invalid access link. Token is missing.');
      return;
    }

    void bindToDevice();
  }, [token]);

  const bindToDevice = async () => {
    setState('binding');
    try {
      const response = await axios.post(
        `/pappv2/api/public/driver-links/${token}/bind`,
        {},
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        if (response?.data?.data) {
          const publicData = response.data.data as RouteData;
          setRouteData(publicData);
          const defaultLeg =
            publicData.legs?.[1]?.id || publicData.legs?.[0]?.id || null;
          setExpandedLegId(defaultLeg);
          setState('success');
          return;
        }

        await fetchRouteData();
      } else {
        setState('error');
        setErrorMessage('Link access denied. Please scan the QR code again.');
      }
    } catch (error: unknown) {
      handleErrorResponse(error);
    }
  };

  const fetchRouteData = async () => {
    setState('loading');
    try {
      const response = await axios.get(
        `/pappv2/api/public/driver-links/${token}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        },
      );

      if (response?.data?.success && response?.data?.data) {
        const publicData = response.data.data as RouteData;
        setRouteData(publicData);
        const defaultLeg =
          publicData.legs?.[1]?.id || publicData.legs?.[0]?.id || null;
        setExpandedLegId(defaultLeg);
        setState('success');
      } else {
        setState('error');
        setErrorMessage('Failed to load route data. Please try again.');
      }
    } catch (error: unknown) {
      handleErrorResponse(error);
    }
  };

  const handleErrorResponse = (error?: unknown) => {
    if (axios.isAxiosError(error)) {
      const apiMessage = error.response?.data?.message;
      if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
        setState('error');
        setErrorMessage(apiMessage);
        return;
      }
    }

    setState('error');
    setErrorMessage(
      'Link access denied. The link may be expired, revoked, invalid, or opened from a different device.',
    );
  };

  const getLegTimeRange = (leg: RouteLegData) => {
    const start = formatTimeValue(leg.scheduledArrival);
    const end = formatTimeValue(leg.scheduledDeparture);
    return `${start}-${end}`;
  };

  const getActualTimeRange = (leg: RouteLegData) => {
    const start = formatTimeValue(leg.actualArrival);
    const end = formatTimeValue(leg.actualDeparture);
    return `${start}-${end}`;
  };

  const getLatestStatusText = (leg: RouteLegData) => {
    if (
      leg.terminalStatusDescription &&
      leg.terminalStatusDescription.trim().length > 0
    ) {
      return leg.terminalStatusDescription.toUpperCase();
    }

    if (leg.terminalStatus && leg.terminalStatus.trim().length > 0) {
      return leg.terminalStatus
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .toUpperCase();
    }

    return '-';
  };

  const getLoadingContainers = (leg: RouteLegData): LoadingContainer[] => {
    const containers: LoadingContainer[] = [];

    const hasValue = (value?: string | null) =>
      !!value && value.trim().length > 0;

    const lc1Available =
      leg.isLoadingInCar ||
      hasValue(leg.carRegistrationNumber) ||
      hasValue(leg.carGateNumber) ||
      hasValue(leg.carContainerNumber);

    if (lc1Available) {
      containers.push({
        type: 'LC1',
        reg: leg.carRegistrationNumber || null,
        container: leg.carContainerNumber || null,
        gate: leg.carGateNumber || leg.gateNumber,
        scheduledTime: `${formatTimeValue(
          leg.scheduledArrival,
        )} - ${formatTimeValue(leg.scheduledDeparture)}`,
        actualTime: `${formatTimeValue(leg.actualArrival)} - ${formatTimeValue(
          leg.actualDeparture,
        )}`,
      });
    }

    const lc2Available =
      leg.isLoadingInTrailer ||
      hasValue(leg.trailerRegistrationNumber) ||
      hasValue(leg.trailerGateNumber) ||
      hasValue(leg.trailerContainerNumber);

    if (lc2Available) {
      containers.push({
        type: 'LC2',
        reg: leg.trailerRegistrationNumber || null,
        container: leg.trailerContainerNumber || null,
        gate: leg.trailerGateNumber || leg.gateNumber,
        scheduledTime: `${formatTimeValue(
          leg.scheduledArrival,
        )} - ${formatTimeValue(leg.scheduledDeparture)}`,
        actualTime: `${formatTimeValue(leg.actualArrival)} - ${formatTimeValue(
          leg.actualDeparture,
        )}`,
      });
    }

    const lc3Gate =
      hasValue(leg.gateNumber) &&
      leg.gateNumber !== leg.carGateNumber &&
      leg.gateNumber !== leg.trailerGateNumber
        ? leg.gateNumber
        : null;

    if (hasValue(lc3Gate)) {
      containers.push({
        type: 'LC3',
        reg: null,
        container: null,
        gate: lc3Gate,
        scheduledTime: `${formatTimeValue(
          leg.scheduledArrival,
        )} - ${formatTimeValue(leg.scheduledDeparture)}`,
        actualTime: `${formatTimeValue(leg.actualArrival)} - ${formatTimeValue(
          leg.actualDeparture,
        )}`,
      });
    }

    return containers;
  };

  const renderContainerCard = (container: LoadingContainer, idx: number) => (
    <Paper
      key={`${container.type}-${idx}`}
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: '8px',
        borderColor: '#ECEEF2',
        backgroundColor: '#FBFBFC',
        mb: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <LocalShippingOutlinedIcon sx={{ color: '#9AA1AD', fontSize: 26 }} />
        <Box sx={{ minWidth: 0, ml: 'auto', textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#4D5562',
              lineHeight: 1.2,
              textAlign: 'right',
            }}
          >
            {container.type} | REG NB | CONTAINER NB
          </Typography>
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 700,
              color: '#171D28',
              lineHeight: 1.2,
              mt: 0.35,
              wordBreak: 'break-word',
              textAlign: 'right',
            }}
          >
            {(container.reg || '-').toUpperCase()} |{' '}
            {(container.container || '-').toUpperCase()}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#D9DCE2' }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.05,
        }}
      >
        <Box
          component="img"
          src={scheduleIcon}
          alt="Schedule and actual time"
          sx={{
            width: 44,
            height: 24,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        <Box sx={{ ml: 'auto', textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: '1.05rem',
              fontWeight: 800,
              color: '#171D28',
              lineHeight: 1.1,
              textAlign: 'right',
            }}
          >
            {container.scheduledTime}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#8E95A3',
              lineHeight: 1.1,
              mt: 0.25,
              textAlign: 'right',
            }}
          >
            {container.actualTime}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#D9DCE2' }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 1.05,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box
            component="img"
            src={gateIcon}
            alt="Gate"
            sx={{
              width: 26,
              height: 16,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.82rem',
              fontWeight: 700,
              color: '#6E7785',
            }}
          >
            GATE
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '2.15rem',
            fontWeight: 900,
            color: '#171D28',
            lineHeight: 1,
          }}
        >
          {container.gate || '-'}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="xs" sx={{ py: 2.5, px: 2 }}>
      {(state === 'loading' || state === 'binding') && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>
            {state === 'binding'
              ? 'Verifying device access...'
              : 'Loading route information...'}
          </Typography>
        </Box>
      )}

      {state === 'success' && routeData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ textAlign: 'center', pt: 0.4, pb: 1.3 }}>
            <Typography
              component="div"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.02em',
                fontSize: '1rem',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              <Box component="span" sx={{ color: '#4CAF2F' }}>
                ROUTE NAME:
              </Box>{' '}
              <Box component="span" sx={{ color: '#2B3441' }}>
                {routeData.routeReference}
              </Box>
            </Typography>
          </Box>

          <Box>
            {(routeData.legs || []).map((leg) => {
              const expanded = expandedLegId === leg.id;
              return (
                <Accordion
                  key={leg.id}
                  expanded={expanded}
                  onChange={(_, isExpanded) =>
                    setExpandedLegId(isExpanded ? leg.id : null)
                  }
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1,
                    borderRadius: 0,
                    '&:before': { display: 'none' },
                    backgroundColor: 'transparent',
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      expanded ? (
                        <ExpandMoreIcon sx={{ color: '#222' }} />
                      ) : (
                        <ChevronRightIcon sx={{ color: '#222' }} />
                      )
                    }
                    sx={{
                      minHeight: 48,
                      px: 0.5,
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        letterSpacing: '0.01em',
                        color: '#4D5562',
                        fontWeight: 600,
                      }}
                    >
                      {`${leg.locationName.toUpperCase()} | DAY ${
                        leg.dayNumber
                      } | ${getLegTimeRange(leg)}`}
                    </Typography>
                  </AccordionSummary>
                  <Divider
                    sx={{ borderColor: expanded ? '#67C23A' : '#C5CAD2' }}
                  />

                  <AccordionDetails sx={{ px: 0, pt: 1.2, pb: 1.5 }}>
                    {leg.isCheckpoint && (
                      <Box sx={{ mb: 1.5 }}>
                        {locationAccessDisabled[leg.id] && (
                          <Alert severity="info" sx={{ mb: 1 }}>
                            Location access is off. If you are comfortable
                            sharing your location, please enable it in your
                            device or browser settings.
                          </Alert>
                        )}
                        <Box
                          role="group"
                          aria-label="Checkpoint events"
                          aria-busy={savingLegId === leg.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 1,
                          }}
                        >
                          {CHECKPOINT_ACTIONS.map((action) => (
                            <ToggleButton
                              key={action.status}
                              value={action.status}
                              selected={!!leg[action.field]}
                              disabled={savingLegId !== null}
                              color="success"
                              onChange={() =>
                                void updateCheckpointStatus(leg, action)
                              }
                              sx={{ flexDirection: 'column' }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  justifyContent: 'space-between',
                                  flexWrap: 'nowrap',
                                  flexDirection: 'row',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                  }}
                                >
                                  {leg[action.field] && (
                                    <CheckIcon
                                      aria-hidden="true"
                                      color="success"
                                      sx={{ flexShrink: 0 }}
                                    />
                                  )}
                                  {action.label}
                                </Box>
                                <Typography component="span" variant="caption">
                                  {formatTimeValue(leg[action.field])}
                                </Typography>
                              </Box>
                            </ToggleButton>
                          ))}
                        </Box>
                        {savingLegId === leg.id && (
                          <Typography
                            role="status"
                            variant="body2"
                            sx={{ mt: 1 }}
                          >
                            Saving checkpoint status...
                          </Typography>
                        )}
                        {updateError?.legId === leg.id && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {updateError.message}
                          </Alert>
                        )}
                      </Box>
                    )}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '4px',
                        borderColor: '#D9DCE2',
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          mb: 1,
                          textAlign: 'left',
                        }}
                      >
                        ARRIVAL AND DEPARTURE
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.4,
                            }}
                          >
                            <LocalShippingOutlinedIcon
                              sx={{ color: '#9AA1AD', fontSize: 26 }}
                            />
                            <LocalShippingOutlinedIcon
                              sx={{ color: '#9AA1AD', fontSize: 26 }}
                            />
                            <LocalShippingOutlinedIcon
                              sx={{ color: '#9AA1AD', fontSize: 26 }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1.3,
                              mt: 0.35,
                              pl: 0.15,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#596171',
                              }}
                            >
                              LC3
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#596171',
                              }}
                            >
                              LC2
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#596171',
                              }}
                            >
                              LC1
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            sx={{
                              fontSize: '1.25rem',
                              lineHeight: 1,
                              fontWeight: 800,
                              color: '#161D28',
                            }}
                          >
                            {getLegTimeRange(leg).replace('-', ' - ')}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '1.1rem',
                              lineHeight: 1,
                              fontWeight: 700,
                              color: '#8E95A3',
                              mt: 0.35,
                            }}
                          >
                            {getActualTimeRange(leg).replace('-', ' - ')}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: '4px',
                        borderColor: '#D9DCE2',
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          mb: 1,
                          textAlign: 'left',
                        }}
                      >
                        LATEST STATUS
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            color: '#8E95A3',
                            fontSize: 28,
                            p: 0.5,
                            bgcolor: '#F0F1F3',
                            borderRadius: '50%',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '1.25rem',
                            lineHeight: 1.2,
                            fontWeight: 900,
                            color: '#171D28',
                            textAlign: 'right',
                          }}
                        >
                          {getLatestStatusText(leg)}
                        </Typography>
                      </Box>
                    </Paper>

                    {getLoadingContainers(leg).map(renderContainerCard)}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          <Paper sx={{ p: 1.5, backgroundColor: '#f7f8fb' }}>
            <Typography variant="body2" color="text.secondary">
              This secure link expires on{' '}
              {formatDateTimeValue(routeData.expiresAt)}.
            </Typography>
          </Paper>
        </Box>
      )}

      {state === 'error' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main' }} />
          <Alert severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
          <Paper sx={{ p: 2, width: '100%', backgroundColor: '#fafafa' }}>
            <Typography variant="body2">
              If you need access on a different device, ask your dispatcher to
              generate a new QR code.
            </Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
}

export default PublicDriverPage;
